export const COURSE = {
  title: "AI-Powered Data Analytics",
  titleAccent: "Professional Program",
  tagline: "Towards Growth",
  overview:
    "A live, mentor-led program engineered for the AI era — blending real-time industry projects, Generative AI, and the exact tools employers demand. Learn to turn raw data into decisions that move businesses forward.",
  badges: [
    { label: "5 Months", sub: "Intensive Program" },
    { label: "Live", sub: "Instructor-Led" },
    { label: "Max 15", sub: "Learners / Batch" },
  ],
  overviewChapters: [
    {
      no: "01",
      title: "Learn Live, Not Recorded",
      body:
        "Every session is delivered live by industry mentors. Ask questions, get unstuck in real time, and learn the way high-performing teams actually work.",
    },
    {
      no: "02",
      title: "Build a Real Portfolio",
      body:
        "Module-based challenges plus two end-to-end industry projects that prove you can solve real business problems — not just pass quizzes.",
    },
    {
      no: "03",
      title: "AI Woven Into Everything",
      body:
        "From prompt engineering to building your own Analyst AI Agents, you'll wield Generative AI across Excel, SQL, Python and visualization.",
    },
    {
      no: "04",
      title: "Career, Handled",
      body:
        "Unlimited AI-powered mock interviews, structured placement assistance, and a dedicated success team that stays with you until you land the role.",
    },
  ],
  learn: [
    { title: "Collect & Clean", desc: "Master data collection, quality and governance so your insights start from solid ground.", span: "lg:col-span-2" },
    { title: "Analyze", desc: "Apply statistics and structured thinking to find the story hidden in the numbers.", span: "" },
    { title: "Visualize", desc: "Design dashboards in Tableau & Power BI that make executives act.", span: "" },
    { title: "Leverage AI", desc: "Use Generative AI and custom agents to automate analysis and 10x your output.", span: "lg:col-span-2" },
  ],
  features: [
    { title: "Live Instructor-Led Learning", desc: "Real mentors, real-time feedback, zero passive video watching." },
    { title: "Maximum 15 Learners Per Batch", desc: "Boutique batch sizes so you're never just a name on a screen." },
    { title: "Real-Time Industry Projects", desc: "Work on problems sourced from actual business scenarios." },
    { title: "AI-Integrated Curriculum", desc: "Generative AI and Analyst Agents built into every module." },
    { title: "Structured Career Assistance", desc: "Mock interviews, resume craft and placement support." },
    { title: "Lifetime Resource Access", desc: "Recordings, materials, assignments and revision content — forever." },
  ],
  tools: [
    { name: "Excel", hint: "Advanced + VBA" },
    { name: "SQL", hint: "Queries to Triggers" },
    { name: "Tableau", hint: "Interactive Dashboards" },
    { name: "Power BI", hint: "DAX + AI Visuals" },
    { name: "Python", hint: "Pandas · NumPy" },
    { name: "Generative AI", hint: "Prompting + Agents" },
    { name: "AI Analyst Agents", hint: "Workflow Automation" },
    { name: "Business Statistics", hint: "Probability · Regression" },
  ],
  curriculum: [
    { phase: "Data Foundation", topics: ["Introduction to Data and Analytics", "Types of Data", "Data Lifecycle", "Database Fundamentals", "Data Quality Concepts", "Data Governance Basics"] },
    { phase: "Advanced Excel", topics: ["Getting Started with Excel", "Cell References, Named Ranges & Functions", "Logical Functions & Conditional Formulas", "Lookup Functions & Nested IF", "Advanced Excel Functions", "Date, Time & Text Functions", "Data Cleaning, Duplicates & Filtering", "Conditional Formatting & Charts", "Pivot Tables & Pivot Charts", "Dashboard Development & Best Practices", "Power Query & Power Pivot", "VBA Programming & Macro Automation"] },
    { phase: "SQL", topics: ["Introduction to SQL & Fundamentals", "Syntax, Commands & Data Types", "DQL & SQL Operators", "CASE Statements & NULL Management", "Aggregate Functions & Grouping", "Database Constraints", "SQL Join Operations", "DDL, DML & TCL", "Indexes & Views", "Stored Procedures", "User-Defined Functions & Control Constructs", "UNION, INTERSECT & Subqueries", "Exception Handling & Triggers"] },
    { phase: "Tableau", topics: ["Tableau Fundamentals", "Chart Creation & Formatting", "Data Filtering Techniques", "Calculated Fields & Expressions", "Data Blending & Joining", "Advanced & Custom Visualizations", "Groups, Bins, Hierarchies, Sets & Parameters", "Analytics Features & Dashboard Design", "Dashboard Interactivity & Tableau Public"] },
    { phase: "Power BI", topics: ["Introduction & Installation", "Interface, Data Sources & Visualizations", "Dashboards with Animated Visuals", "AI-Powered Visualizations", "Interactive Visualizations", "Power Query Editor Essentials", "Data Modeling", "Data Transformation & Customization", "DAX Formulas & Expressions", "Custom Visual Development", "Power BI Service & Cloud Publishing"] },
    { phase: "Python", topics: ["Introduction & Installation", "Data Structures", "Control Statements", "Functions", "Regex & File Handling", "Logging, DateTime, Math, JSON, MySQL", "Object-Oriented Programming", "Web Scraping", "Pandas", "NumPy & Matplotlib"] },
    { phase: "Generative AI", topics: ["Introduction to Generative AI", "Prompt Engineering Techniques", "Generative AI in Excel", "Python Development with Generative AI", "Generative AI for SQL Coding", "Generative AI for Data Analysis & Visualization"] },
    { phase: "Analyst AI Agent Development", topics: ["Introduction to Analyst AI Agents", "KPI Monitoring Agents", "Reporting Agents", "Data Quality Agents", "Forecasting Agents", "Multi-Agent Analytics Ecosystem"] },
    { phase: "Business Statistics", topics: ["Descriptive Statistics", "Probability & Normal Distribution", "Inferential Statistics", "Data Cleaning & Insights"] },
  ],
  partner: {
    name: "Intellectra AI",
    role: "Technology Partner",
    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/ia10zbrl_Intellectra%20AI.png",
    desc: "Every project you build is a live, real-time problem statement powered by our technology partner Intellectra AI — real data, real stakes, real experience.",
  },
  portfolio: {
    points: [
      {
        title: "A Project With Every Module",
        desc: "You don't just learn a tool — you ship with it. Each module ends in a hands-on project, so you gain practical, in-hand experience from every module and every tool as you go.",
      },
      {
        title: "2 End-to-End Industry Projects",
        desc: "Two complete, real-world projects that take you from raw data all the way to decision-ready insight — the exact kind of work recruiters want to see.",
      },
      {
        title: "100% Real-Time Projects",
        desc: "No sample datasets. All projects are real-time industry projects from our technology partner Intellectra AI.",
      },
    ],
  },
  internship: {
    intro:
      "Learn the way real analytics teams work — on live problem statements, with real data, tools and deadlines. Your project work doubles as genuine industry experience.",
    points: [
      "Real-time industry projects sourced from Intellectra AI",
      "Internship-style experience on live problem statements",
      "Work end-to-end: collect, clean, analyze, visualize, present",
      "Portfolio-ready deliverables you can showcase to employers",
    ],
    stats: [
      { value: "6", label: "Module Projects" },
      { value: "2", label: "End-to-End Projects" },
      { value: "100%", label: "Real-Time Data" },
    ],
  },
  placement: {
    intro:
      "Skills get you in the room — our Career Success ecosystem gets you the offer. Structured placement training includes in-person mock interviews as part of the course with detailed personalised feedback, plus additional access to our AI mock interview platform for unlimited practice.",
    items: [
      { title: "Resume Building Session", desc: "Craft a recruiter-ready resume that puts your real project work front and centre." },
      { title: "LinkedIn Profile Optimisation", desc: "Build a standout profile that gets you noticed by recruiters and hiring managers." },
      { title: "In-Person Mock Interviews", desc: "Face-to-face mock interviews as part of the course, with detailed personalised feedback." },
      { title: "AI Mock Interview Platform", desc: "Additional unlimited practice on our PlacementPilot AI platform, anytime you want." },
      { title: "Job Portal Access", desc: "Curated internships and full-time roles you can apply to directly, in a few clicks." },
      { title: "Dedicated Success Team", desc: "Real mentors who stay with you from your first module to your first offer." },
    ],
    steps: [
      { label: "Placement Training", sub: "Resume + LinkedIn" },
      { label: "In-Person Mock Interviews", sub: "Detailed feedback" },
      { label: "AI Practice", sub: "Unlimited on PlacementPilot" },
      { label: "Job Portal", sub: "Apply to curated roles" },
      { label: "Offer", sub: "Land the role" },
    ],
  },
  included: {
    intro: "One program, everything you need — from your first live class to your first analytics offer.",
    items: [
      "Live instructor-led training (5 months)",
      "Maximum 15 learners per batch",
      "9-phase AI-integrated curriculum",
      "A hands-on project with every module",
      "2 end-to-end real-time industry projects",
      "Internship experience with Intellectra AI",
      "Unlimited AI-powered mock interviews",
      "Resume & portfolio building",
      "Structured placement assistance",
      "Lifetime access to recordings & resources",
      "Mentorship from experienced industry experts",
      "Completion, project & internship certificates",
    ],
  },
  certifications: {
    intro:
      "You don't finish with one certificate — you finish with a stack. Dual course-completion certification (co-branded with Texas Tech University), real end-to-end project certificates, and a certificate for every module-based project — all from our technology partner Intellectra AI.",
    issuers: ["ExcellenC", "Texas Tech University", "Intellectra AI"],
    groups: [
      {
        label: "Course Completion",
        note: "Dual, co-branded program certification.",
        items: [
          {
            title: "AI-Powered Data Analytics Professional Program",
            issuer: "ExcellenC",
            tag: "Course Completion",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/qp6x979y_ExcellenC%20-%20AI-Powered%20Data%20Analytics.png",
          },
          {
            title: "AI-Powered Data Analytics Professional Program",
            issuer: "Texas Tech University",
            tag: "Course Completion",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/cuo0psyo_Texas%20Tech%20University%20-%20AI%20Powered%20Data%20Analytics.png",
          },
        ],
      },
      {
        label: "End-to-End Industry Projects",
        note: "Complete real-world projects, powered by Intellectra AI.",
        items: [
          {
            title: "Global Logistics & Port Operations Analytics",
            issuer: "Intellectra AI",
            tag: "End-to-End Project",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/w8ea5r5b_End%20to%20End%20Project%201.png",
          },
          {
            title: "Retail Chain Profit Leakage & Demand Optimization",
            issuer: "Intellectra AI",
            tag: "End-to-End Project",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/4jei4i1f_End%20to%20End%20Project%202.png",
          },
        ],
      },
      {
        label: "Module-Based Project Certificates",
        note: "A real project certificate for every tool you master.",
        items: [
          {
            title: "Financial, Marketing & Operational Analysis",
            issuer: "Intellectra AI",
            tag: "Excel",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/zs9fy1e9_Excel%20Project.png",
          },
          {
            title: "EdTech Subscription Analytics Platform",
            issuer: "Intellectra AI",
            tag: "SQL",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/cy1bb7m5_SQL%20Project.png",
          },
          {
            title: "Hospital Patient Analytics & Outcome Dashboard",
            issuer: "Intellectra AI",
            tag: "Tableau",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/s2eep9f3_Tableau%20Project.png",
          },
          {
            title: "RLS-Enabled Regional Sales & Profit Analytics",
            issuer: "Intellectra AI",
            tag: "Power BI",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/htatyahx_Power%20BI%20Project.png",
          },
          {
            title: "Product Insights via Web Scraping & Analysis",
            issuer: "Intellectra AI",
            tag: "Python",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/4wip04xq_Python%20Project.png",
          },
          {
            title: "Business Analyst AI Copilot",
            issuer: "Intellectra AI",
            tag: "Generative & Agentic AI",
            img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/sesk2gx8_Gen%20AI%20and%20Agentic%20AI%20Project.png",
          },
        ],
      },
    ],
    letters: {
      title: "Internship Letters — Included",
      intro: "Beyond certificates, your enrollment comes with official internship documentation for the entire program duration.",
      items: [
        { label: "Internship Offer Letter", when: "Issued during enrollment" },
        { label: "Internship Completion Letter", when: "Issued at course completion" },
        { label: "Internship Experience Letter", when: "Covering the entire course duration" },
      ],
    },
  },
  mockInterview: {
    product: "PlacementPilot",
    by: "by ExcellenC",
    tagline: "Accelerate your journey from learning to landing your dream career.",
    intro:
      "PlacementPilot is ExcellenC's AI-powered Career Success Platform — practise unlimited voice-enabled mock interviews, get instant AI feedback on every answer, and walk into every real interview with confidence.",
    stats: [
      { value: "5", label: "Interview Rounds" },
      { value: "15", label: "Questions / Session" },
      { value: "3", label: "Career Tracks" },
      { value: "AI", label: "Powered Feedback" },
    ],
    rounds: ["Technical", "Case Study", "Behavioral / HR", "Final Review", "Salary Negotiation"],
    features: [
      { title: "AI-Powered Evaluation", desc: "Every answer is scored out of 10 with specific strengths and improvements." },
      { title: "Voice-Enabled", desc: "Speak your answers out loud — a true-to-life interview experience." },
      { title: "Course-Aligned Questions", desc: "Questions adapt to your track: Data Analytics, Business Analytics, Data Science & AI." },
      { title: "Full 5-Round Journey", desc: "From technical rounds all the way to salary negotiation." },
      { title: "Model Answers", desc: "Compare your response to an ideal expected answer, every time." },
      { title: "Integrated Job Portal", desc: "Explore internships and full-time roles without leaving the platform." },
    ],
    gallery: [
      { caption: "Landing Experience", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/u0jmgzt9_Placement%20Pilot%20-%20Intro%20Page.JPG" },
      { caption: "Practice Smart, Get Placed Faster", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/y2jarrdq_Placement%20Pilot%20-%20Sign%20In%20page.JPG" },
      { caption: "Choose Your Career Track", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/6efv5vm3_Placement%20Pilot%20Home%20Page.JPG" },
      { caption: "Voice-Enabled Interview", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/mtqgo7ft_Placement%20Pilot%20Interview%20Page%201.JPG" },
      { caption: "AI Evaluation & Feedback", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/n8twal2z_Placement%20Pilot%20Interview%20Page%202.JPG" },
      { caption: "Integrated Job Portal", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_analytics-journey-8/artifacts/t1qz12xh_Placement%20Pilot%20-%20Job%20Portal.JPG" },
    ],
  },
  careers: [
    "Data Analyst", "Business Intelligence Analyst", "Reporting Analyst", "MIS Analyst",
    "Analytics Specialist", "BI Developer", "Dashboard Analyst", "Tableau Developer",
    "Power BI Analyst", "Data Visualisation Specialist", "AI Data Analyst", "Analytics Consultant",
    "Data Quality Analyst", "Insights Analyst", "Decision Support Analyst",
  ],
  comingSoon: [
    { title: "Demo & Intro Videos", desc: "Sample sessions and a full program walkthrough to see the experience first-hand." },
  ],
  whoShouldEnroll:
    "Graduates, final-year students, career switchers and working professionals ready to build or advance a career in Analytics & Data Science.",
  fee: {
    price: 40000,
    currency: "₹",
    note: "including GST",
    tagline: "No hyped prices. No inflated MRPs. No fake discounts — just one straightforward fee for the full program.",
    enrollmentNote: "Contact your relationship manager to complete the enrollment procedures.",
    highlights: [
      "All 9 phases + 2 end-to-end industry projects",
      "Lifetime access to recordings & resources",
      "Unlimited AI-powered mock interviews",
      "Structured placement assistance",
    ],
  },
};

export const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_65815994-a89c-4272-8a24-c0034bad28ed/artifacts/1sz6r2jw_ExcellenC%20logo%20May%2017%202025.png";
